/**
 * Fallback offset (px) when `#mainNav` is missing or not laid out yet.
 * Approximate desktop baseline; programmatic scroll uses **measured** nav bounds
 * so mobile / `.scrolled` / breakpoint padding still align.
 * Keep `scroll-padding-top` on `html` (App.css) in the same ballpark for hash-only navigation.
 */
export const NAVBAR_OFFSET_PX = 52;

const NAV_ID = "mainNav";

const DEFAULT_OPTIONS = {
  behavior: "smooth",
  preflight: true,
  preflightStableFrames: 4,
  preflightMaxWaitMs: 260,
  verify: true,
  correctionDelayMs: 10,
  settleDurationMs: 10,
  maxWaitMs: 2200,
  maxCorrections: 4,
  tolerancePx: 2,
};

let activeScrollToken = 0;

/**
 * Distance from the top of the layout viewport to the first pixel below the fixed navbar.
 * Uses bounding rect bottom (not a fixed 52px) so mobile breakpoints, `.scrolled`, and
 * subpixel layout match the real bar height.
 */
function getNavbarOffsetPx() {
  const nav = document.getElementById(NAV_ID);
  if (!nav) return NAVBAR_OFFSET_PX;
  const bottom = nav.getBoundingClientRect().bottom;
  if (!Number.isFinite(bottom) || bottom < 24) return NAVBAR_OFFSET_PX;
  return Math.round(bottom);
}

function getTargetY(el) {
  const offset = getNavbarOffsetPx();
  return Math.max(
    0,
    Math.round(el.getBoundingClientRect().top + window.scrollY - offset),
  );
}

function waitForTargetStable(token, el, opts, onReady) {
  const start = performance.now();
  let stableFrames = 0;
  let lastTargetY = getTargetY(el);

  const tick = () => {
    if (token !== activeScrollToken || !document.contains(el)) return;

    const nextTargetY = getTargetY(el);
    if (Math.abs(nextTargetY - lastTargetY) <= 1) {
      stableFrames += 1;
    } else {
      stableFrames = 0;
      lastTargetY = nextTargetY;
    }

    const stableEnough = stableFrames >= opts.preflightStableFrames;
    const timedOut = performance.now() - start >= opts.preflightMaxWaitMs;
    if (stableEnough || timedOut) {
      onReady(nextTargetY);
      return;
    }

    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}

function waitForScrollSettled(token, opts, onSettled) {
  const start = performance.now();
  let lastMove = start;
  let lastY = window.scrollY;

  const tick = () => {
    if (token !== activeScrollToken) return;

    const now = performance.now();
    const currentY = window.scrollY;
    if (Math.abs(currentY - lastY) > 0.5) {
      lastMove = now;
      lastY = currentY;
    }

    const settled = now - lastMove >= opts.settleDurationMs;
    const timedOut = now - start >= opts.maxWaitMs;
    if (settled || timedOut) {
      onSettled();
      return;
    }

    requestAnimationFrame(tick);
  };

  requestAnimationFrame(tick);
}

function runCorrection(token, el, opts, remaining) {
  if (token !== activeScrollToken || !document.contains(el)) return;

  const expectedY = getTargetY(el);
  const delta = expectedY - window.scrollY;
  if (Math.abs(delta) <= opts.tolerancePx || remaining <= 0) return;

  // Final snap is intentional: aligns using current measured navbar bottom.
  window.scrollTo({ top: expectedY, behavior: "auto" });

  window.setTimeout(() => {
    runCorrection(token, el, opts, remaining - 1);
  }, opts.correctionDelayMs);
}

/**
 * Scrolls the **window** so the element with `sectionId` is positioned at the top of
 * the viewport **below** the fixed navbar (`#mainNav`), using its live
 * `getBoundingClientRect().bottom` as the offset (not a fixed 52px).
 *
 * Behavior:
 * 1) Smooth-scroll toward computed target.
 * 2) Wait until scrolling settles (or timeout).
 * 3) Verify position and self-correct if needed (handles layout shift races).
 *
 * Scope: single-page anchor navigation only. Does not scroll modal bodies, iframes,
 * or project cards by link id — those call sites use their own logic if needed.
 *
 * @param {string} sectionId - Document element id (no `#`). No-op if missing.
 * @param {Partial<typeof DEFAULT_OPTIONS>} options - Optional tuning overrides.
 */
export function scrollToSection(sectionId, options = {}) {
  const el = document.getElementById(sectionId);
  if (!el) return;

  const opts = { ...DEFAULT_OPTIONS, ...options };
  const token = ++activeScrollToken;
  const startScroll = (targetY) => {
    if (token !== activeScrollToken || !document.contains(el)) return;
    window.scrollTo({ top: targetY, behavior: opts.behavior });

    if (!opts.verify) return;
    waitForScrollSettled(token, opts, () => {
      window.setTimeout(() => {
        runCorrection(token, el, opts, opts.maxCorrections);
      }, opts.correctionDelayMs);
    });
  };

  if (opts.preflight) {
    waitForTargetStable(token, el, opts, startScroll);
    return;
  }

  startScroll(getTargetY(el));
}
